import { apiJson, createIdempotencyKey } from '../api';

type Candidate = { applicationId: string; name: string; phone: string; position: string; employmentType: string; outletCode: string; stage: string; offerStatus: string; updatedAt: string };
type CandidatePage = { items: Candidate[]; page: number; pageSize: number; total: number };
type HrIdentity = { email: string };
type CandidateDetail = {
  applicant: Record<string, string>;
  stage: string;
  offerStatus: string;
  outletCode: string;
  role: string;
  candidateFolderLink: string;
  links: Record<string, string>;
  linkStatus?: Record<string, string>;
  values: Record<string, string>;
};

let currentPage = 1;
let selectedId = '';
let identity: HrIdentity | null = null;

export async function renderHrPage(root: HTMLElement): Promise<void> {
  try {
    identity = await apiJson<HrIdentity>('/api/hr/me');
  } catch {
    renderLogin(root);
    return;
  }
  renderDesk(root);
  await load(root);
}

function renderLogin(root: HTMLElement): void {
  root.innerHTML = `
    <div class="shell auth-shell">
      <div class="topbar"><div><div class="brand">Stupiak Recruitment</div><h1>HR Login</h1></div></div>
      <form id="hrLoginForm" class="card auth-card">
        <div class="field"><label>Email</label><input id="hrEmail" type="email" autocomplete="email" required></div>
        <div class="field"><label>HR Passcode</label><input id="hrPasscode" type="password" autocomplete="current-password" required></div>
        <div class="actions"><button class="primary" type="submit">Login</button></div>
        <div id="loginResult"></div>
      </form>
    </div>`;
  root.querySelector<HTMLFormElement>('#hrLoginForm')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const result = root.querySelector<HTMLElement>('#loginResult')!;
    const button = root.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    result.className = '';
    result.textContent = '';
    button.disabled = true;
    try {
      identity = await apiJson<HrIdentity>('/api/hr/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: root.querySelector<HTMLInputElement>('#hrEmail')?.value || '',
          passcode: root.querySelector<HTMLInputElement>('#hrPasscode')?.value || '',
        }),
      });
      renderDesk(root);
      await load(root);
    } catch (error) {
      result.className = 'notice error';
      result.textContent = error instanceof Error ? error.message : String(error);
    } finally {
      button.disabled = false;
    }
  });
}

function renderDesk(root: HTMLElement): void {
  root.innerHTML = `
    <div class="shell">
      <div class="topbar">
        <div><div class="brand">Stupiak Recruitment</div><h1>HR Recruitment Desk</h1></div>
        <div class="session-chip"><span>${safe(identity?.email || '')}</span><button id="logout" class="secondary" type="button">Logout</button></div>
      </div>
      <div class="card">
        <div class="grid three">
          <div class="field"><label>Outlet</label><select id="outlet"><option value="">All allowed outlets</option><option value="KCH">KCH - Royal Richmond Square</option><option value="JDBM">JDBM - Jalan Datuk Bandar Mustapha</option></select></div>
          <div class="field"><label>Stage</label><input id="stage" placeholder="All stages"></div>
          <div class="field"><label>Search</label><input id="query" placeholder="Name, phone or ID"></div>
        </div>
        <div class="actions"><button id="prev" class="secondary" type="button">Previous</button><button id="next" class="secondary" type="button">Next</button><button id="load" class="primary" type="button">Search</button></div>
      </div>
      <div class="workbench">
        <section class="card"><div id="result">Loading...</div></section>
        <section class="card"><div id="detail">Select a candidate.</div></section>
      </div>
    </div>`;
  root.querySelector('#load')?.addEventListener('click', () => { currentPage = 1; load(root); });
  root.querySelector('#prev')?.addEventListener('click', () => { currentPage = Math.max(1, currentPage - 1); load(root); });
  root.querySelector('#next')?.addEventListener('click', () => { currentPage += 1; load(root); });
  root.querySelector('#logout')?.addEventListener('click', async () => {
    await apiJson('/api/hr/logout', { method: 'POST' }).catch(() => undefined);
    identity = null;
    selectedId = '';
    currentPage = 1;
    renderLogin(root);
  });
}

async function load(root: HTMLElement): Promise<void> {
  const outlet = root.querySelector<HTMLSelectElement>('#outlet')?.value || '';
  const stage = root.querySelector<HTMLInputElement>('#stage')?.value || '';
  const q = root.querySelector<HTMLInputElement>('#query')?.value || '';
  const target = root.querySelector<HTMLElement>('#result')!;
  target.className = '';
  target.textContent = 'Loading...';
  try {
    const page = await apiJson<CandidatePage>(`/api/hr/candidates?page=${currentPage}&pageSize=25&outlet=${encodeURIComponent(outlet)}&stage=${encodeURIComponent(stage)}&q=${encodeURIComponent(q)}`);
    target.innerHTML = `
      <div class="table-wrap"><table class="data-table"><thead><tr><th>Candidate</th><th>WhatsApp</th><th>Position</th><th>Type</th><th>Outlet</th><th>Stage</th><th>Offer</th><th>Updated</th></tr></thead><tbody>
      ${page.items.map(item => `<tr data-id="${safe(item.applicationId)}" class="${item.applicationId === selectedId ? 'selected' : ''}"><td><b>${safe(item.name)}</b><br><small>${safe(item.applicationId)}</small></td><td>${safe(item.phone)}</td><td>${safe(item.position)}</td><td>${safe(item.employmentType)}</td><td>${safe(item.outletCode)}</td><td>${safe(item.stage)}</td><td>${safe(item.offerStatus)}</td><td>${safe(item.updatedAt)}</td></tr>`).join('')}
      </tbody></table></div><p>Page ${page.page} · ${page.total} candidates</p>`;
    target.querySelectorAll<HTMLTableRowElement>('tr[data-id]').forEach((row) => {
      row.addEventListener('click', () => {
        selectedId = row.dataset.id || '';
        void loadDetail(root, selectedId);
      });
    });
  } catch (error) {
    target.className = 'notice error';
    target.textContent = error instanceof Error ? error.message : String(error);
  }
}

async function loadDetail(root: HTMLElement, applicationId: string): Promise<void> {
  const detail = root.querySelector<HTMLElement>('#detail')!;
  detail.className = '';
  detail.textContent = 'Loading candidate...';
  try {
    const data = await apiJson<CandidateDetail>(`/api/hr/candidate/${encodeURIComponent(applicationId)}`);
    renderDetail(root, applicationId, data);
  } catch (error) {
    detail.className = 'notice error';
    detail.textContent = error instanceof Error ? error.message : String(error);
  }
}

function renderDetail(root: HTMLElement, applicationId: string, data: CandidateDetail): void {
  const app = data.applicant || {};
  const values = data.values || {};
  const links = data.links || {};
  const canOffer = data.role !== 'interviewer';
  const detail = root.querySelector<HTMLElement>('#detail')!;
  detail.innerHTML = `
    <div class="detail-head">
      <div><h2>${safe(app.name || 'Candidate')}</h2><p>${safe(applicationId)} · ${safe(data.outletCode)} · ${safe(data.stage || '-')}</p></div>
      <div class="status-pill">${safe(data.offerStatus || 'NO OFFER')}</div>
    </div>
    <div class="tabs">
      <button class="secondary" data-tab="overview" type="button">Overview</button>
      <button class="secondary" data-tab="application" type="button">Application & CV</button>
      <button class="secondary" data-tab="interview" type="button">Interview</button>
      ${canOffer ? '<button class="secondary" data-tab="offer" type="button">Offer</button>' : ''}
      <button class="secondary" data-tab="audit" type="button">Audit</button>
    </div>
    <div id="detailTab"></div>`;
  detail.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((button) => {
    button.addEventListener('click', () => showTab(root, applicationId, data, button.dataset.tab || 'overview'));
  });
  showTab(root, applicationId, data, data.role === 'interviewer' ? 'interview' : 'overview');
}

function showTab(root: HTMLElement, applicationId: string, data: CandidateDetail, tab: string): void {
  const app = data.applicant || {};
  const values = data.values || {};
  const links = data.links || {};
  const hasLegacyOffer = isLegacyOfferLink(data);
  const target = root.querySelector<HTMLElement>('#detailTab')!;
  if (tab === 'application') {
    target.innerHTML = linkList([
      ['Candidate Folder', data.candidateFolderLink],
      ['Application PDF', links.applicationPdf],
      ['CV', links.cv],
    ]);
    return;
  }
  if (tab === 'interview') {
    target.innerHTML = `
      <div class="grid">
        <div class="field"><label>Interview Date</label><input id="interviewDate" type="date" value="${safeAttr(values.interviewDate)}"></div>
        <div class="field"><label>Interview Time</label><input id="interviewTime" type="time" value="${safeAttr(values.interviewTime)}"></div>
        <div class="field"><label>Duration</label><input id="interviewDuration" type="number" value="${safeAttr(values.interviewDuration || '30')}"></div>
        <div class="field"><label>Method</label><input id="interviewMethod" value="${safeAttr(values.interviewMethod || 'In person')}"></div>
        <div class="field full"><label>Location / Link</label><input id="interviewLocation" value="${safeAttr(values.interviewLocation)}"></div>
        <div class="field full"><label>Interviewer Email</label><input id="interviewer" value="${safeAttr(values.assignedInterviewerEmail)}"></div>
        <div class="field full"><label>Notes</label><textarea id="interviewNotes">${safe(values.interviewNotes)}</textarea></div>
      </div>
      <div class="actions"><button id="saveInterview" class="primary" type="button">Prepare Interview WhatsApp</button></div>
      <div class="section-title">Review</div>
      <div class="grid">
        ${scoreField('communicationScore', values.communicationScore)}
        ${scoreField('attitudeScore', values.attitudeScore)}
        ${scoreField('availabilityScore', values.availabilityScore)}
        ${scoreField('experienceScore', values.experienceScore)}
        ${scoreField('overallScore', values.overallScore)}
        <div class="field"><label>Attendance</label><select id="attendance"><option>${safe(values.attendance || 'Attended')}</option><option>Attended</option><option>No-show</option></select></div>
        <div class="field full"><label>Recommendation</label><input id="recommendation" value="${safeAttr(values.recommendation)}"></div>
        <div class="field full"><label>Review Notes</label><textarea id="reviewNotes">${safe(values.reviewNotes)}</textarea></div>
      </div>
      <div class="actions"><button id="saveReview" class="primary" type="button">Save Review</button></div>
      <div id="actionResult"></div>`;
    root.querySelector('#saveInterview')?.addEventListener('click', () => runAction(root, applicationId, 'interview.save', readFields(root, ['interviewDate','interviewTime','interviewDuration','interviewMethod','interviewLocation','interviewer','interviewNotes'])));
    root.querySelector('#saveReview')?.addEventListener('click', () => runAction(root, applicationId, 'review.save', { ...readFields(root, ['attendance','communicationScore','attitudeScore','availabilityScore','experienceScore','overallScore','recommendation','reviewNotes']), reviewedBy: '' }));
    return;
  }
  if (tab === 'offer') {
    target.innerHTML = `
      ${hasLegacyOffer ? '<div class="notice warning"><b>Legacy Offer Link</b><br>This candidate still has an old Google Apps Script signing link. Generate the offer again here to replace it with a Cloudflare offer link.</div>' : ''}
      <div class="grid">
        <div class="field"><label>Decision</label><select id="decision"><option>Ready to Send</option><option>Prepare Draft Only</option><option>Reject / Hold</option></select></div>
        <div class="field"><label>Employment Type</label><select id="employmentType"><option>${safe(values.offerEmploymentType || app.employmentType || 'Part-Time')}</option><option>Part-Time</option><option>Full-Time</option></select></div>
        <div class="field"><label>Position</label><input id="position" value="${safeAttr(values.offerPosition || app.position)}"></div>
        <div class="field"><label>NRIC / Passport</label><input id="nric" value="${safeAttr(app.icNumber)}"></div>
        <div class="field"><label>Start Date</label><input id="startDate" type="date" value="${safeAttr(values.startDate)}"></div>
        <div class="field"><label>Acceptance Deadline</label><input id="acceptanceDeadline" type="date" value="${safeAttr(values.acceptanceDeadline)}"></div>
        <div class="field full"><label>Work Location</label><input id="workLocation" value="${safeAttr(values.workLocation)}"></div>
        <div class="field"><label>Reporting To</label><input id="reportingTo" value="${safeAttr(values.reportingTo)}"></div>
        <div class="field"><label>Probation / Training</label><input id="probationPeriod" value="${safeAttr(values.probationPeriod)}"></div>
        <div class="field"><label>Resignation Notice</label><input id="resignationNotice" value="${safeAttr(values.resignationNotice)}"></div>
        <div class="field"><label>Company Signatory</label><input id="companySignatoryName" value="${safeAttr(values.companySignatoryName)}"></div>
        <div class="field"><label>Signatory Title</label><input id="companySignatoryTitle" value="${safeAttr(values.companySignatoryTitle)}"></div>
        <div class="field"><label>PT Start Rate</label><input id="partTimeStartRate" value="${safeAttr(values.partTimeStartRate)}"></div>
        <div class="field"><label>PT Junior Rate</label><input id="partTimeJuniorRate" value="${safeAttr(values.partTimeJuniorRate)}"></div>
        <div class="field full"><label>PT Schedule Basis</label><input id="partTimeSchedulingBasis" value="${safeAttr(values.partTimeSchedulingBasis)}"></div>
        <div class="field full"><label>PT Expected Availability</label><input id="partTimeExpectedAvailability" value="${safeAttr(values.partTimeExpectedAvailability)}"></div>
        <div class="field"><label>FT Probation Salary</label><input id="fullTimeProbationSalary" value="${safeAttr(values.fullTimeProbationSalary)}"></div>
        <div class="field"><label>FT Confirmed Salary</label><input id="fullTimeConfirmedSalary" value="${safeAttr(values.fullTimeConfirmedSalary)}"></div>
        <div class="field full"><label>FT Working Hours</label><input id="fullTimeWorkingHours" value="${safeAttr(values.fullTimeWorkingHours)}"></div>
        <div class="field full"><label>FT Rest Day</label><input id="fullTimeRestDay" value="${safeAttr(values.fullTimeRestDay)}"></div>
        <div class="field full"><label>FT Leave</label><input id="fullTimeLeave" value="${safeAttr(values.fullTimeLeave)}"></div>
        <div class="field full"><label>Statutory Contributions</label><input id="statutoryContributions" value="${safeAttr(values.statutoryContributions)}"></div>
      </div>
      <div class="actions"><button id="generateOffer" class="primary" type="button">${hasLegacyOffer ? 'Regenerate Cloudflare Offer' : 'Generate Offer'}</button></div>
      ${linkList([['Unsigned Offer PDF', links.unsignedOfferPdf], ['Offer WhatsApp', links.offerWhatsApp]], data.linkStatus)}
      <div id="actionResult"></div>`;
    root.querySelector('#generateOffer')?.addEventListener('click', () => runAction(root, applicationId, 'offer.generate', readFields(root, ['decision','employmentType','position','nric','startDate','acceptanceDeadline','workLocation','reportingTo','probationPeriod','resignationNotice','companySignatoryName','companySignatoryTitle','partTimeStartRate','partTimeJuniorRate','partTimeSchedulingBasis','partTimeExpectedAvailability','fullTimeProbationSalary','fullTimeConfirmedSalary','fullTimeWorkingHours','fullTimeRestDay','fullTimeLeave','statutoryContributions'])));
    return;
  }
  if (tab === 'audit') {
    target.innerHTML = linkList([
      ['Signed Offer PDF', links.signedOfferPdf],
      ['Audit Record', links.audit],
      ['Send Signed Copy via WhatsApp', links.signedWhatsApp],
    ]);
    return;
  }
  target.innerHTML = `
    <dl class="summary"><dt>Name</dt><dd>${safe(app.name)}</dd><dt>Mobile</dt><dd>${safe(app.phone || app.mobile)}</dd><dt>Position</dt><dd>${safe(app.position)}</dd><dt>Employment Type</dt><dd>${safe(app.employmentType)}</dd><dt>Available</dt><dd>${safe(app.dateAvailable || app.availableDate)}</dd></dl>
    ${hasLegacyOffer ? '<div class="notice warning">Old offer signing link detected. Open the Offer tab and regenerate it before sending to applicant.</div>' : ''}
    ${linkList([['Interview WhatsApp', links.interviewWhatsApp], ['Offer WhatsApp', links.offerWhatsApp]], data.linkStatus)}`;
}

async function runAction(root: HTMLElement, applicationId: string, action: string, payload: Record<string, string>): Promise<void> {
  const result = root.querySelector<HTMLElement>('#actionResult')!;
  result.className = '';
  result.textContent = 'Saving...';
  try {
    const response = await apiJson<{ message?: string; whatsAppUrl?: string }>(`/api/hr/action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, applicationId, idempotencyKey: createIdempotencyKey(action), payload }),
    });
    const buttons = response.whatsAppUrl ? `<div class="actions"><button id="copyWa" class="secondary" type="button">Copy WhatsApp Link</button><a class="button primary" href="${safeAttr(response.whatsAppUrl)}" target="_blank" rel="noreferrer">Open WhatsApp</a></div>` : '';
    result.className = 'notice success';
    result.innerHTML = `${safe(response.message || 'Saved.')} ${buttons}`;
    root.querySelector('#copyWa')?.addEventListener('click', () => navigator.clipboard.writeText(response.whatsAppUrl || ''));
    await load(root);
  } catch (error) {
    result.className = 'notice error';
    result.textContent = error instanceof Error ? error.message : String(error);
  }
}

function readFields(root: HTMLElement, ids: string[]): Record<string, string> {
  const payload: Record<string, string> = {};
  ids.forEach((id) => {
    const field = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#${id}`);
    payload[id] = field?.value || '';
  });
  return payload;
}

function scoreField(id: string, value: unknown): string {
  return `<div class="field"><label>${safe(id.replace(/Score$/, ' Score'))}</label><input id="${safeAttr(id)}" type="number" min="1" max="5" value="${safeAttr(value || '3')}"></div>`;
}

function linkList(items: [string, unknown][], status: Record<string, string> = {}): string {
  return `<div class="link-list">${items.map(([label, url]) => {
    const key = String(label);
    if (status[key] === 'legacy') return `<span>${safe(label)} legacy - regenerate Cloudflare offer</span>`;
    return url ? `<a href="${safeAttr(url)}" target="_blank" rel="noreferrer">${safe(label)}</a>` : `<span>${safe(label)} unavailable</span>`;
  }).join('')}</div>`;
}

function isLegacyOfferLink(data: CandidateDetail): boolean {
  const raw = String(data.links?.offerWhatsApp || '');
  let decoded = raw;
  try { decoded = decodeURIComponent(raw); } catch {}
  return data.linkStatus?.['Offer WhatsApp'] === 'legacy' || /script\.google\.com\/macros/i.test(raw) || /script\.google\.com\/macros/i.test(decoded);
}

function safe(value: unknown): string { return String(value || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]!)); }
function safeAttr(value: unknown): string { return safe(value); }
